import { ReportHelper } from "../../logic/report-helper";
import { ReportModel } from "../../models/report/ReportModel";
import { MatchModel } from "../../models/report/MatchModel";

import { TemplateModel } from "../../models/template/TemplateModel";
import { LitElement, html, css, unsafeCSS, property } from "lit-element";

import stratusStatsReportStyles from './stratus-stats-report.scss';
import { StratusStatsCardTemplateModel } from "../../models/components/StratusStatsCardTemplateModel";
import { StratusStatsCardComponentTemplateModel } from "../../models/components/StratusStatsCardComponentTemplateModel";
import { StratusStatsCardTeamTemplateModel } from "../../models/components/StratusStatsCardTeamTemplateModel";
import { StratusStatsCard } from "../stratus-stats-card/stratus-stats-card";
import { ComponentMetricValueModel } from "../../models/template/ComponentMetricValueModel";
import MetricValueCalculationType from "../../enums/metric-value-calculation-type";
import { RowModel } from "../../models/template/RowModel";
import { ColumnModel } from "../../models/template/ColumnModel";
import { ComponentModel } from "../../models/template/ComponentModel";
import CardComponentType from "../../enums/card-component-type";
import { StratusStatsCardPlayerTemplateModel } from "../../models/components/StratusStatsCardPlayerTemplateModel";
import { StratusStatsCardTableHeadingTemplateModel } from "../../models/components/StratusStatsCardTableHeadingTemplateModel";
import { TeamMetaDataModel } from "../../models/report/meta-data/TeamMetaDataModel";
import { TeamModel } from "../../models/report/TeamModel";
import ReportType from "../../enums/report-type";

import playersIcon from "../../assets/ic_rugbyplayer.svg";
import teamsIcon from "../../assets/ic_stats.svg";
import { reportsApi } from "../../api/reportsApi";
import { StratusStatsColumnValuesModel } from "../../models/components/StratusStatsColumnValuesModel";

import { List } from 'linqts';
import { PlayerModel } from "../../models/report/PlayerModel";
import { MetricModel } from "../../models/report/MetricModel";
import MetricConditionalFormatType from "../../enums/metric-conditional-format-type";
import { StratusStatsCardBaseMetricTemplateModel } from "../../models/components/StratusStatsCardBaseMetricTemplateModel";

import Consts from "../../consts/consts";

export class StratusStatsReport extends LitElement {
    public _template: TemplateModel;
    private _report: ReportModel;
    private _reportHelper: ReportHelper;
    private _extraStyles: string;
    private _matchId: string;
    private _seasonId: string;
    private _logoTop: boolean;
    private _showTeams: boolean;
    private _showPlayerStats: boolean;
    private _reportType: number;
    private _playerStatsReport: StratusStatsReport;
    private _reportsApi: reportsApi;
    private _playlistClickThrough: boolean;
    private _userId: string;
    private _reportId: string;

    @property({ type: String })
    private _isPrimaryColorDark: String;

    @property({ type: Boolean })
    private _showTeamStats: boolean;

    constructor(
        matchId: string,
        seasonId: string,
        template: TemplateModel,
        report: ReportModel,
        extraStyles: string,
        logoTop: boolean,
        showTeams: boolean,
        showPlayerStats: boolean,
        reportType: number,
        playerStatsReport: StratusStatsReport,
        reportsApi: reportsApi,
        playlistClickThrough: boolean,
        userId: string,
        reportId: string) {
        super();

        this._matchId = matchId;
        this._seasonId = seasonId;
        this._template = template;
        this._report = report;
        this._reportHelper = new ReportHelper(report);
        this._logoTop = logoTop;
        this._showTeams = showTeams;
        this._showPlayerStats = showPlayerStats;
        this._reportType = reportType;
        this._playerStatsReport = playerStatsReport;
        this._isPrimaryColorDark = "light";
        this._extraStyles = extraStyles;
        this._reportsApi = reportsApi;
        this._playlistClickThrough = playlistClickThrough;
        this._userId = userId;
        this._reportId = reportId;

        this._showTeamStats = true;
    }

    static get styles() {
        return [
            css`${unsafeCSS(stratusStatsReportStyles)}`,
        ]
    }

    sortRowColumns(a, b) {
        if (a.order > b.order)
            return 1;
        else if (a.order < b.order)
            return -1;
        else
            return 0;
    }

    getTeamColumnValues(teamMetricValues: ComponentMetricValueModel[]) {
        let teams = [];

        for (let [i, team] of Object.entries(this._reportHelper.getTeams())) {
            let teamTemplate = new StratusStatsCardTeamTemplateModel('', '', '', team.teamMetaData.name, [], team.teamId, null, team.group);
            for (let [j, teamMetricValue] of Object.entries(teamMetricValues)) {
                let metricObject = this._reportHelper.getTeamMetric(team.teamId, teamMetricValue.valueMetricId);
                let value = metricObject?.value ?? 0;
                let metric = value ? value.toString() : '-';

                let metricTotalObject = this._reportHelper.getTeamMetric(team.teamId, teamMetricValue.totalMetricId);
                let total = (metricTotalObject?.value ?? 0).toString();

                switch (teamMetricValue.type) {
                    case MetricValueCalculationType.FractionValue:
                        metric = `${value} / ${total}`;
                        break;
                    case MetricValueCalculationType.PercentageCalculation:
                        let calc = value / parseInt(total) * 100;
                        metric = `${calc}%`;
                        break;
                    case MetricValueCalculationType.Value:
                        metric = value == 0 ? '-' : value.toString();
                        break;
                    case MetricValueCalculationType.PercentageValue:
                        metric = value == 0 ? '0%' : `${(value * 100).toFixed(0)}%`;
                        break;
                    case MetricValueCalculationType.Distance:
                        if (value < 1) {
                            metric = `-`;
                            break;
                        }
                        metric = `${value.toFixed(0)}`;
                        break;
                    case MetricValueCalculationType.RateValue:
                        let calcRateValue = parseFloat((value * teamMetricValue.rateValue).toFixed(2));
                        metric = value == 0 ? '-' : calcRateValue.toString();
                        break;
                }

                teamTemplate.metricId = teamMetricValue.valueMetricId;
                teamTemplate.displayValue = metric;
                teamTemplate.columnValues.push(new StratusStatsColumnValuesModel(metric, metricObject?.isMax ?? false, metricObject?.isMin ?? false, metricObject?.isWithinThreshold ?? false));
            }

            teams.push(teamTemplate);
        }

        return teams;
    }

    getTeamPlayers(teamId: string, playerMetricValues: ComponentMetricValueModel[]) {
        let teamPlayers = [];

        for (let [j, player] of Object.entries(this._reportHelper.getTeamPlayers(teamId))) {

            if (!player.playerMetaData) {
                console.log(`PlayerId of ${player.playerId} has no MetaData`);
                continue;
            }

            if (!player.playerMetaData.displayName)
                player.playerMetaData.displayName = player.playerMetaData.fullName;

            let playerTemplate = new StratusStatsCardPlayerTemplateModel(player.playerMetaData.matchTeamPlayer.startingNumber, `${player.playerMetaData.displayName}`, player.playerMetaData.firstName, player.playerMetaData.lastName, player.playerId, player.group, player.playerMetaData.teams);

            for (let [k, playerMetricValue] of Object.entries(playerMetricValues)) {
                let metricObject = this._reportHelper.getTeamPlayerMetric(teamId, player.playerId, playerMetricValue.valueMetricId);
                let value = metricObject?.value ?? 0;

                if (!value) {
                    playerTemplate.columnValues.push(new StratusStatsColumnValuesModel(`-`, false, false, false));
                }
                else {
                    switch (playerMetricValue.type) {
                        case MetricValueCalculationType.PercentageValue:
                            value = parseInt((value * 100).toFixed(0));
                            playerTemplate.columnValues.push(new StratusStatsColumnValuesModel(`${value}%`, metricObject.isMax, metricObject.isMin, metricObject.isWithinThreshold));
                            break;
                        case MetricValueCalculationType.Time:
                            let minutes = Math.floor(value / 60000);
                            let seconds = ((value % 60000) / 1000).toFixed(0);
                            playerTemplate.columnValues.push(new StratusStatsColumnValuesModel(`${minutes}:${(parseInt(seconds) < 10 ? '0' : '') + seconds}`, metricObject.isMax, metricObject.isMin, metricObject.isWithinThreshold));
                            break;
                        case MetricValueCalculationType.Distance:

                            if (value < 1) {
                                playerTemplate.columnValues.push(new StratusStatsColumnValuesModel(`-`, false, false, false));
                                break;
                            }

                            value = parseInt(value.toFixed(0));
                            playerTemplate.columnValues.push(new StratusStatsColumnValuesModel(`${value}`, metricObject.isMax, metricObject.isMin, metricObject.isWithinThreshold));
                            break;
                        case MetricValueCalculationType.RateValue:
                            value = parseFloat((value * playerMetricValue.rateValue).toFixed(2));
                            playerTemplate.columnValues.push(new StratusStatsColumnValuesModel(`${value}`, metricObject.isMax, metricObject.isMin, metricObject.isWithinThreshold));
                            break;
                        default:
                            playerTemplate.columnValues.push(new StratusStatsColumnValuesModel(`${value}`, metricObject.isMax, metricObject.isMin, metricObject.isWithinThreshold));
                            break;
                    }
                }

                playerTemplate.metricId = playerMetricValue.valueMetricId;
                playerTemplate.value = value;
            }

            teamPlayers.push(playerTemplate);
        }

        return teamPlayers;
    }

    getPlayers(playerMetricValues: ComponentMetricValueModel[]) {
        let players = [];

        for (let [j, player] of Object.entries(this._report.players)) {

            if (!player.playerMetaData) {
                console.log(`PlayerId of ${player.playerId} has no MetaData`);
                continue;
            }

            if (!player.playerMetaData.displayName)
                player.playerMetaData.displayName = player.playerMetaData.fullName;

            let playerTemplate = new StratusStatsCardPlayerTemplateModel(0, `${player.playerMetaData.displayName}`,player.playerMetaData.firstName, player.playerMetaData.lastName, player.playerId, player.group, player.playerMetaData.teams);

            for (let [k, playerMetricValue] of Object.entries(playerMetricValues)) {
                let metricObject = this._reportHelper.getPlayerMetric(player.playerId, playerMetricValue.valueMetricId);
                let value = metricObject?.value ?? 0;

                if (!value) {
                    playerTemplate.columnValues.push(new StratusStatsColumnValuesModel(`-`, false, false, false));
                }
                else {
                    switch (playerMetricValue.type) {
                        case MetricValueCalculationType.PercentageValue:
                            value = parseInt((value * 100).toFixed(0));
                            playerTemplate.columnValues.push(new StratusStatsColumnValuesModel(`${value}%`, metricObject.isMax, metricObject.isMin, metricObject.isWithinThreshold));
                            break;
                        case MetricValueCalculationType.Time:
                            let minutes = Math.floor(value / 60000);
                            let seconds = ((value % 60000) / 1000).toFixed(0);
                            playerTemplate.columnValues.push(new StratusStatsColumnValuesModel(`${minutes}:${(parseInt(seconds) < 10 ? '0' : '') + seconds}`, metricObject.isMax, metricObject.isMin, metricObject.isWithinThreshold));
                            break;
                        case MetricValueCalculationType.Distance:

                            if (value < 1) {
                                playerTemplate.columnValues.push(new StratusStatsColumnValuesModel(`-`, false, false, false));
                                break;
                            }

                            playerTemplate.columnValues.push(new StratusStatsColumnValuesModel(`${value.toFixed(0)}`, metricObject.isMax, metricObject.isMin, metricObject.isWithinThreshold));
                            break;
                        case MetricValueCalculationType.RateValue:
                            value = parseFloat((value * playerMetricValue.rateValue).toFixed(2));
                            playerTemplate.columnValues.push(new StratusStatsColumnValuesModel(`${value}`, metricObject.isMax, metricObject.isMin, metricObject.isWithinThreshold));
                            break;
                        default:
                            playerTemplate.columnValues.push(new StratusStatsColumnValuesModel(`${value}`, metricObject.isMax, metricObject.isMin, metricObject.isWithinThreshold));
                            break;
                    }
                }

                playerTemplate.metricId = playerMetricValue.valueMetricId;
                playerTemplate.value = value;
            }

            players.push(playerTemplate);
        }

        return players;
    }

    getTeam(teamId: string): TeamModel {
        return this._report.teams.find(team => team.teamId == teamId);
    }

    getTeamName(teamId: string): string {
        let team = this.getTeam(teamId);

        return team?.teamMetaData.name ?? '';
    }

    getTeamMetricValues(teamId: string, metricCalculationModel: ComponentMetricValueModel) {
        let displayValue = "";
        let percentageValue = 0;

        let metricObject = this._reportHelper.getTeamMetric(teamId, metricCalculationModel.valueMetricId);
        let value = metricObject?.value ?? 0;

        let total = null;
        if (metricCalculationModel.totalMetricId) {
            let totalMetric = this._reportHelper.getTeamMetric(teamId, metricCalculationModel.totalMetricId);
            total = totalMetric?.value ?? 0;
        }

        switch (metricCalculationModel.type) {
            case MetricValueCalculationType.FractionValue:
                displayValue = `${value} / ${total}`;
                percentageValue = value / parseInt(total) * 100;
                break;
            case MetricValueCalculationType.PercentageCalculation:
                let calc = value / parseInt(total) * 100;
                displayValue = `${calc}%`;
                percentageValue = calc;
                break;
            case MetricValueCalculationType.Value:
                displayValue = value.toString();
                break;
            case MetricValueCalculationType.PercentageValue:
                displayValue = `${(value * 100).toFixed(0)}%`;
                percentageValue = value;
                break;
            case MetricValueCalculationType.Distance:
                if (value < 1) {
                    displayValue = `-`;
                    break;
                }
                displayValue = `${value.toFixed(0)}`;
                break;
            case MetricValueCalculationType.RateValue:
                value = parseFloat((value * metricCalculationModel.rateValue).toFixed(2));
                displayValue = value.toString();
                break;
        }

        if (Number.isNaN(percentageValue) || !percentageValue)
            percentageValue = 0;

        return {
            displayValue: displayValue,
            percentageValue: percentageValue,
            metricValue: value
        }
    }

    getBaseMetricValues(metricCalculationModel: ComponentMetricValueModel) {
        let displayValue = "";
        let percentageValue = 0;

        let metricObject = this._reportHelper.getBaseMetric(this._matchId, metricCalculationModel.valueMetricId);
        let value = metricObject?.value ?? 0;

        let total = null;
        if (metricCalculationModel.totalMetricId) {
            let totalMetric = this._reportHelper.getBaseMetric(this._matchId, metricCalculationModel.totalMetricId);
            total = totalMetric?.value ?? 0;
        }

        switch (metricCalculationModel.type) {
            case MetricValueCalculationType.FractionValue:
                displayValue = `${value} / ${total}`;
                percentageValue = value / parseInt(total) * 100;
                break;
            case MetricValueCalculationType.PercentageCalculation:
                let calc = value / parseInt(total) * 100;
                displayValue = `${calc}%`;
                percentageValue = calc;
                break;
            case MetricValueCalculationType.Value:
                let decimal = value % 1 !== 0;

                if (decimal) {
                    displayValue = value.toFixed(2);
                }
                else {
                    displayValue = value.toString();
                }
                break;
            case MetricValueCalculationType.PercentageValue:
                displayValue = `${(value * 100).toFixed(0)}%`;
                percentageValue = value;
                break;
            case MetricValueCalculationType.Distance:
                if (value < 1) {
                    displayValue = `-`;
                    break;
                }
                displayValue = `${value.toFixed(0)}`;
                break;
            case MetricValueCalculationType.RateValue:
                value = parseFloat((value * metricCalculationModel.rateValue).toFixed(2));
                displayValue = value.toString();
                break;
            case MetricValueCalculationType.Time:
                let minutes = Math.floor(value / 60000);
                let seconds = ((value % 60000) / 1000).toFixed(0);
                displayValue = `${minutes}:${(parseInt(seconds) < 10 ? '0' : '') + seconds}`;
                break;
        }

        if (isNaN(percentageValue) || !percentageValue)
            percentageValue = 0;

        return {
            displayValue: displayValue,
            percentageValue: percentageValue,
            metricValue: value
        }
    }

    // TODO: Get Match metric values

    buildComponents(heading: string, components: ComponentModel[]) {
        let cardTemplate = new StratusStatsCardTemplateModel(heading);

        components.sort(this.sortRowColumns);

        let match: MatchModel = this._reportHelper.getMatch(this._matchId);
        let reportName = this._template.name;
        let seasonName = this._reportHelper.getSeasonName();
        let seriesName = this._reportHelper.getSeriesName();
        let removeComponentsPadding = false;

        for (let [i, component] of Object.entries(components)) {
            let cardComponent = new StratusStatsCardComponentTemplateModel(
                component.type,
                this._template.baseColor,
                this._template.secondaryColor,
                component.heading,
                component.highlightRule,
                '#ffffff',
                component.topEntityAmount,
                component.showStartingNumber,
                component.showFullMetricName,
                component.showExportToCSV);

            cardComponent.reportName = reportName;
            cardComponent.seriesName = seriesName;
            cardComponent.seasonName = seasonName;

            if (component.awayTeamMetricValue && component.awayTeamMetricValue.valueMetricId != Consts.EmptyGuid && component.homeTeamMetricValue && component.homeTeamMetricValue.valueMetricId != Consts.EmptyGuid) {
                removeComponentsPadding = false;

                let awayTeamMetricValue = this.getTeamMetricValues(match.matchMetaData.awayTeamID, component.awayTeamMetricValue);
                let homeTeamMetricValue = this.getTeamMetricValues(match.matchMetaData.homeTeamID, component.homeTeamMetricValue);

                let awayTeamName = this.getTeamName(match.matchMetaData.awayTeamID);
                let homeTeamName = this.getTeamName(match.matchMetaData.homeTeamID);

                if (component.type == CardComponentType.HorizontalBarMeterEndStart) {
                    let total = awayTeamMetricValue.metricValue + homeTeamMetricValue.metricValue;

                    awayTeamMetricValue.percentageValue = (awayTeamMetricValue.metricValue / total) * 100;
                    homeTeamMetricValue.percentageValue = (homeTeamMetricValue.metricValue / total) * 100;
                } else if (component.type == CardComponentType.PlayerBreakdownTable) {
                    //Top X Breakdown Table                    
                    cardComponent.homeTeamPlayers = this.getTeamPlayers(match.matchMetaData.homeTeamID, [component.homeTeamMetricValue]);
                    cardComponent.awayTeamPlayers = this.getTeamPlayers(match.matchMetaData.awayTeamID, [component.awayTeamMetricValue]);
                }

                let teams = this._reportHelper.getTeams();

                let awayTeam = teams.find(s => s.teamId == (match.matchMetaData.awayTeamID));
                let homeTeam = teams.find(s => s.teamId == (match.matchMetaData.homeTeamID));

                cardComponent.awayTeam = new StratusStatsCardTeamTemplateModel(
                    awayTeamMetricValue.displayValue,
                    awayTeamMetricValue.percentageValue,
                    this._template.awayTeam.color,
                    awayTeamName,
                    [],
                    match.matchMetaData.awayTeamID,
                    component.awayTeamMetricValue.valueMetricId,
                    awayTeam.group);

                cardComponent.homeTeam = new StratusStatsCardTeamTemplateModel(
                    homeTeamMetricValue.displayValue,
                    homeTeamMetricValue.percentageValue,
                    this._template.homeTeam.color,
                    homeTeamName,
                    [],
                    match.matchMetaData.homeTeamID,
                    component.homeTeamMetricValue.valueMetricId,
                    homeTeam.group);

            } else if (component.playerMetricValues && component.playerMetricValues.length > 0) {
                removeComponentsPadding = true;

                if (match != null) {
                    let teams = this._reportHelper.getTeams();

                    let awayTeam = teams.find(s => s.teamId == (match.matchMetaData.awayTeamID));
                    let homeTeam = teams.find(s => s.teamId == (match.matchMetaData.homeTeamID));

                    let awayTeamName = this.getTeamName(match.matchMetaData.awayTeamID);
                    let homeTeamName = this.getTeamName(match.matchMetaData.homeTeamID);

                    component.playerMetricValues.sort(this.sortRowColumns);
                    component.tableHeaders.sort(this.sortRowColumns);

                    /* Conditional Formatting */
                    // Away Team
                    let awayPlayers = new List<PlayerModel>(this._reportHelper.getTeamPlayers(match.matchMetaData.awayTeamID));
                    if (awayPlayers.FirstOrDefault()?.group) {
                        let groupArray: string[] = []
                        awayPlayers.Select(p => p.group).Distinct().ForEach(p => groupArray.push(p));

                        for (let [j, group] of Object.entries(groupArray)) {
                            let groupPlayers = awayPlayers.Where(p => p.group == group);
                            let metrics = groupPlayers.SelectMany(p => new List<MetricModel>(p.metrics));
                            this.conditionalFormatMetrics(metrics, component.playerMetricValues);
                        }
                    }
                    else {
                        let metrics = awayPlayers.SelectMany(p => new List<MetricModel>(p.metrics));
                        this.conditionalFormatMetrics(metrics, component.playerMetricValues);
                    }
                    // Home Team
                    let homePlayers = new List<PlayerModel>(this._reportHelper.getTeamPlayers(match.matchMetaData.homeTeamID));
                    if (homePlayers.FirstOrDefault()?.group) {
                        let groupArray: string[] = []
                        homePlayers.Select(p => p.group).Distinct().ForEach(p => groupArray.push(p));

                        for (let [j, group] of Object.entries(groupArray)) {
                            let groupPlayers = homePlayers.Where(p => p.group == group);
                            let metrics = groupPlayers.SelectMany(p => new List<MetricModel>(p.metrics));
                            this.conditionalFormatMetrics(metrics, component.playerMetricValues);
                        }
                    }
                    else {
                        let metrics = homePlayers.SelectMany(p => new List<MetricModel>(p.metrics));
                        this.conditionalFormatMetrics(metrics, component.playerMetricValues);
                    }
                    /* Conditional Formatting */

                    // BREAKDOWN TABLE                    
                    cardComponent.homeTeamPlayers = this.getTeamPlayers(match.matchMetaData.homeTeamID, component.playerMetricValues);
                    cardComponent.awayTeamPlayers = this.getTeamPlayers(match.matchMetaData.awayTeamID, component.playerMetricValues);

                    cardComponent.awayTeam = new StratusStatsCardTeamTemplateModel(null, null, this._template.awayTeam.color, awayTeamName, [], match.matchMetaData.awayTeamID, null, awayTeam.group);
                    cardComponent.homeTeam = new StratusStatsCardTeamTemplateModel(null, null, this._template.homeTeam.color, homeTeamName, [], match.matchMetaData.homeTeamID, null, homeTeam.group);

                    for (let [j, tableHeader] of Object.entries(component.tableHeaders)) {
                        cardTemplate.tableHeaders.push(new StratusStatsCardTableHeadingTemplateModel(tableHeader.fullName, tableHeader.abbreviation, tableHeader.order, component.playerMetricValues[j].valueMetricId));
                    }
                }
                else {
                    component.playerMetricValues.sort(this.sortRowColumns);
                    component.tableHeaders.sort(this.sortRowColumns);

                    /* Conditional Formatting */
                    let players = new List<PlayerModel>(this._report.players);
                    if (players.FirstOrDefault()?.group) {
                        let groupArray: string[] = []
                        players.Select(p => p.group).Distinct().ForEach(p => groupArray.push(p));

                        for (let [j, group] of Object.entries(groupArray)) {

                            let groupPlayers = players.Where(p => p.group == group);
                            let metrics = groupPlayers.SelectMany(p => new List<MetricModel>(p.metrics));
                            this.conditionalFormatMetrics(metrics, component.playerMetricValues);
                        }
                    }
                    else {
                        let metrics = players.SelectMany(p => new List<MetricModel>(p.metrics));
                        this.conditionalFormatMetrics(metrics, component.playerMetricValues);
                    }
                    /* Conditional Formatting */

                    cardComponent.players = this.getPlayers(component.playerMetricValues);

                    for (let [j, tableHeader] of Object.entries(component.tableHeaders)) {
                        cardTemplate.tableHeaders.push(new StratusStatsCardTableHeadingTemplateModel(tableHeader.fullName, tableHeader.abbreviation, tableHeader.order, component.playerMetricValues[j].valueMetricId));
                    }
                }
            } else if (component.teamMetricValues && component.teamMetricValues.length > 0) {
                removeComponentsPadding = true;

                component.teamMetricValues.sort(this.sortRowColumns);
                component.tableHeaders.sort(this.sortRowColumns);

                /* Conditional Formatting */
                let teams = new List<TeamModel>(this._report.teams);

                if (teams.FirstOrDefault()?.group) {
                    let groupArray: string[] = []
                    teams.Select(p => p.group).Distinct().ForEach(p => groupArray.push(p));

                    for (let [j, group] of Object.entries(groupArray)) {
                        let groupTeams = teams.Where(p => p.group == group);
                        let metrics = groupTeams.SelectMany(p => new List<MetricModel>(p.metrics));
                        this.conditionalFormatMetrics(metrics, component.teamMetricValues);
                    }
                }
                else {
                    let metrics = teams.SelectMany(p => new List<MetricModel>(p.metrics));
                    this.conditionalFormatMetrics(metrics, component.teamMetricValues);
                }
                /* Conditional Formatting */

                cardComponent.teams = this.getTeamColumnValues(component.teamMetricValues);

                for (let [j, tableHeader] of Object.entries(component.tableHeaders)) {
                    cardTemplate.tableHeaders.push(new StratusStatsCardTableHeadingTemplateModel(tableHeader.fullName, tableHeader.abbreviation, tableHeader.order, component.teamMetricValues[j].valueMetricId));
                }
            } else if (component.baseMetricValue && component.baseMetricValue.valueMetricId != Consts.EmptyGuid) {
                // Match level metrics
                removeComponentsPadding = false;

                let metricValue = this.getBaseMetricValues(component.baseMetricValue);
                cardComponent.baseMetric = new StratusStatsCardBaseMetricTemplateModel(component.baseMetricValue.valueMetricId, metricValue.displayValue, metricValue.metricValue, metricValue.percentageValue);
            }

            cardTemplate.content.push(cardComponent);
        }

        return new StratusStatsCard(cardTemplate, removeComponentsPadding, this._reportsApi, this._playlistClickThrough, this._userId, this._reportId, this._matchId, this._seasonId);
    }

    buildColumn(column: ColumnModel, columnLength: number) {
        let htmlContent = [];

        let columnWidthPercentage = `${(100 / columnLength)}%`;

        if (column.rows && column.rows.length > 0) {
            htmlContent.push(html`<div class="report-column embedded-column" style="flex-basis:${columnWidthPercentage};" >
                ${this.buildRows(column.rows)}
            </div>`);
        } else {
            htmlContent.push(html`<div class="report-column" style="flex-basis:${columnWidthPercentage};">
                ${html`${this.buildComponents(column.heading, column.components)}`}
            </div>`);
        }

        return htmlContent;
    }

    buildRow(row: RowModel) {
        let htmlContent = [];

        row.columns.sort(this.sortRowColumns);

        htmlContent.push(html`<div class="report-row">
            ${row.columns.map((column, index) => html`${this.buildColumn(column, row.columns.length)}`)}
        </div>`);

        return htmlContent;
    }

    buildRows(rows: RowModel[]) {
        let htmlContent = [];

        this._template.rows.sort(this.sortRowColumns);
        htmlContent.push(rows.map((row, index) => html`${this.buildRow(row)}`));

        return htmlContent;
    }

    firstUpdated() {
        let computedStyle = getComputedStyle(this);

        let primaryValue = computedStyle.getPropertyValue("--table-primary-color");
        let secondaryValue = computedStyle.getPropertyValue("--table-secondary-color");
        let textColorValue = computedStyle.getPropertyValue("--table-text-color");

        let tablePrimaryColor;
        let tableSecondaryColor;
        let textColor;

        switch (this._reportType) {
            case ReportType.TeamMatchScoreSummary:
            case ReportType.MatchReport:
            case ReportType.TeamMatchReport: {
                tablePrimaryColor = this._template.homeTeam.color;
                tableSecondaryColor = this._template.awayTeam.color;
                break;
            }
            case ReportType.PlayerMatchReport:
                tablePrimaryColor = this._template.awayTeam.color;
                tableSecondaryColor = this._template.homeTeam.color;
                break;
            case ReportType.SeasonReport:
            case ReportType.TeamSeasonReport:
            case ReportType.PlayerSeasonReport: {
                tablePrimaryColor = this._template.baseColor;
                tableSecondaryColor = this._template.secondaryColor;
                break;
            }
            // TODO: Match Report
        }

        textColor = "#121212";

        if (!primaryValue) {
            this.style.setProperty("--table-primary-color", tablePrimaryColor);
            this._isPrimaryColorDark = this.lightOrDark(tablePrimaryColor);
        } else
            this._isPrimaryColorDark = this.lightOrDark(primaryValue);

        if (!secondaryValue) {
            this.style.setProperty("--table-secondary-color", tableSecondaryColor);
        }

        if (!textColorValue) {
            this.style.setProperty("--table-text-color", textColor);
        }
    }

    buildTeamHeader() {
        let match: MatchModel = this._reportHelper.getMatch(this._matchId);

        let awayTeam: TeamModel = this.getTeam(match.matchMetaData.awayTeamID);
        let homeTeam: TeamModel = this.getTeam(match.matchMetaData.homeTeamID);

        return html`
            <div class="teams-container">
                <div class="team-container home-team">                    
                    <h3>${homeTeam.teamMetaData.name}</h3>    
                    <div class="team-bubble" style="background-color:${this._template.homeTeam.color}">&nbsp;</div>
                    ${this._showPlayerStats && this._showTeamStats ? html`<a @click="${(event) => this.playerStatsToggle()}" class="view-player-stats-button ${this._isPrimaryColorDark}">${playersIcon}</a>` : ''}
                    ${this._showPlayerStats && !this._showTeamStats ? html`<a @click="${(event) => this.playerStatsToggle()}" class="view-player-stats-button ${this._isPrimaryColorDark}">${teamsIcon}</a>` : ''}
                    <h3 class="score">${homeTeam.score ?? homeTeam?.teamMetaData.matchTeam?.score}</h3>
                </div>
                <h3 class="score">-</h3>
                <div class="team-container away-team">        
                    <h3 class="score">${awayTeam.score ?? awayTeam?.teamMetaData.matchTeam?.score}</h3>            
                    ${this._showPlayerStats && this._showTeamStats ? html`<a @click="${(event) => this.playerStatsToggle()}" class="view-player-stats-button ${this._isPrimaryColorDark}">${playersIcon}</a>` : ''}
                    ${this._showPlayerStats && !this._showTeamStats ? html`<a @click="${(event) => this.playerStatsToggle()}" class="view-player-stats-button ${this._isPrimaryColorDark}">${teamsIcon}</a>` : ''}
                    <div class="team-bubble" style="background-color:${this._template.awayTeam.color}">&nbsp;</div>
                    <h3>${awayTeam.teamMetaData.name}</h3>                    
                </div>
            </div>`;
    }

    async playerStatsToggle() {
        this._showTeamStats = !this._showTeamStats;
    }

    buildPlayerStats() {
        return this._playerStatsReport;
    }

    render() {
        return html`${this._showTeams && this._matchId ? html`${this.buildTeamHeader()}` : ''}
            ${this._showTeamStats ? this.buildRows(this._template.rows) : ''}
            ${!this._showTeamStats ? this.buildPlayerStats() : ''}`
    }

    lightOrDark(color) {
        let r, g, b, hsp;

        // Check the format of the color, HEX or RGB?
        if (color.match(/^rgb/)) {

            // If HEX --> store the red, green, blue values in separate variables
            color = color.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)$/);

            r = color[1];
            g = color[2];
            b = color[3];
        }
        else {

            // If RGB --> Convert it to HEX: http://gist.github.com/983661
            color = +("0x" + color.slice(1).replace(
                color.length < 5 && /./g, '$&$&'
            )
            );

            r = color >> 16;
            g = color >> 8 & 255;
            b = color & 255;
        }

        // HSP (Highly Sensitive Poo) equation from http://alienryderflex.com/hsp.html
        hsp = Math.sqrt(
            0.299 * (r * r) +
            0.587 * (g * g) +
            0.114 * (b * b)
        );

        // Using the HSP value, determine whether the color is light or dark
        if (hsp > 127.5)
            return 'light';
        else
            return 'dark';
    }

    conditionalFormatMetrics(metrics: List<MetricModel>, metricValues: ComponentMetricValueModel[]) {
        for (let [mv, metricValue] of Object.entries(metricValues)) {


            if (!metricValue.valueMetricId)
                continue;

            let groupedMetrics = metrics.Where(m => m.metricId == metricValue.valueMetricId && m.value != 0);
            let groupedMetricValues = groupedMetrics.Select(m => m.value);

            let max = groupedMetricValues.Max();
            let min = groupedMetricValues.Min();
            let threshold = groupedMetricValues.Average();

            let metricArray: MetricModel[] = [];
            groupedMetrics.ForEach(m => metricArray.push(m));

            for (let [l, metric] of Object.entries(metricArray)) {

                if (!metric.value)
                    continue;

                metric.isMax = false;
                metric.isMin = false;
                metric.isWithinThreshold = false;

                if (metricValue.formatType == MetricConditionalFormatType.HigherValueIsBetter) {
                    if (metric.value == max) {
                        metric.isMax = true;
                    }
                    else if (metric.value == min) {
                        metric.isMin = true;
                    }
                    else if (metric.value < threshold) {
                        metric.isWithinThreshold = true;
                    }
                }
                else if (metricValue.formatType == MetricConditionalFormatType.LowerValueIsBetter) {
                    if (metric.value == max) {
                        metric.isMin = true;
                    }
                    else if (metric.value == min) {
                        metric.isMax = true;
                    }
                    else if (metric.value > threshold) {
                        metric.isWithinThreshold = true;
                    }
                }
            }
        }
    }
}

window.customElements.define('stratus-stats-report', StratusStatsReport as any);